// Compact form of a face vector for the PERSISTED query cache.
//
// A piece-work face roster is ~1,100 vectors of 128 floats. As number[] that is
// ~132 JSON nodes per row, which blows isPersistSafe's 20,000-node budget, so
// the roster was never saved and face matching died after a cold offline start.
// One base64 string per vector is ~3 nodes per row, so the same budget holds
// ~6,000 vectors. The ['piecework-face-vectors', tenantId] key caches
// CachedFaceRow[]; the hook's select decodes back to EnrolledVector[].
//
// Float32 is NOT exact: the vectors are L2-normalised in f64 and the RPC
// returns double precision. Rounding each component to f32 moves a distance
// by ~1e-7 at most, against match bands 0.05 apart, so it cannot change which
// worker a face matches (the test pins this on a real f64 unit vector).
//
// Pure and Chrome-86-safe: no Buffer, no spread over a large array.
import type { EnrolledVector } from '@/lib/faceRecognition'

export interface CachedFaceRow { worker_number: string; v: string }

/** Encode floats as little-endian Float32 bytes, base64. */
export function encodeVector(vector: readonly number[]): string {
  const buf = new ArrayBuffer(vector.length * 4)
  const dv = new DataView(buf)
  for (let i = 0; i < vector.length; i++) dv.setFloat32(i * 4, vector[i], true)
  const bytes = new Uint8Array(buf)
  let bin = ''
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
  return btoa(bin)
}

/** Inverse of encodeVector. */
export function decodeVector(s: string): number[] {
  const bin = atob(s)
  const n = bin.length >> 2
  const dv = new DataView(new ArrayBuffer(n * 4))
  for (let i = 0; i < n * 4; i++) dv.setUint8(i, bin.charCodeAt(i))
  const out = new Array<number>(n)
  for (let i = 0; i < n; i++) out[i] = dv.getFloat32(i * 4, true)
  return out
}

/** RPC rows → cached rows. The ONE mapper both writers of the key use
 *  (usePieceworkFaceVectors and the offline prep task). */
export function rosterToCached(data: unknown): CachedFaceRow[] {
  const rows = (data ?? []) as { worker_number?: unknown; vector?: unknown }[]
  const out: CachedFaceRow[] = []
  for (const r of rows) {
    if (!r.worker_number || !Array.isArray(r.vector)) continue
    out.push({ worker_number: r.worker_number as string, v: encodeVector(r.vector as number[]) })
  }
  return out
}

/** Cached rows → the shape consumers use. Module-level so select is stable. */
export function cachedToEnrolled(rows: readonly CachedFaceRow[] | undefined): EnrolledVector[] {
  return (rows ?? []).map(r => ({ worker_number: r.worker_number, vector: decodeVector(r.v) }))
}
